<template>
  <div :class="$style.inlineCheckout">
    <div
      v-if="loading && !ready"
      :class="$style.loadingOverlay"
    >
      <div :class="$style.spinner"></div>
      <p class="type-body-medium">Loading checkout...</p>
    </div>
    <div
      v-if="error"
      :class="$style.error"
    >
      <p class="type-body-medium">{{ error }}</p>
      <Button
        :variant="ButtonVariant.Outline"
        :size="ButtonSize.Medium"
        text="Retry"
        @click="retryCheckout"
      />
    </div>
    <div
      class="paddle-checkout-container"
      :class="$style.checkoutFrame"
    ></div>
  </div>
</template>

<script setup lang="ts">
  import Button from '~/components/Button.vue'
  import { ButtonVariant, ButtonSize } from '~/constants/buttonTypes'
  import { usePaddle } from '~/composables/usePaddle'

  interface Props {
    transactionId: string
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    complete: [data: any]
    close: []
    error: [error: string]
  }>()

  const loading = ref(true)
  const error = ref('')
  const ready = ref(false)

  const { ensurePaddle, onPaddleEvent } = usePaddle()

  // Unsubscribe handle for the shared Paddle event dispatcher (usePaddle owns the single
  // Paddle.Initialize + eventCallback; we subscribe rather than initialize ourselves).
  let unsubscribeEvents: (() => void) | null = null
  // Safety fallback: if checkout.loaded never arrives, force the overlay away so it can't hang.
  let loadedFallbackTimer: ReturnType<typeof setTimeout> | null = null

  const clearLoadedFallback = () => {
    if (loadedFallbackTimer) {
      clearTimeout(loadedFallbackTimer)
      loadedFallbackTimer = null
    }
  }

  const markReady = () => {
    clearLoadedFallback()
    loading.value = false
    ready.value = true
  }

  const handlePaddleEvent = (event: any) => {
    if (!event?.name) return

    switch (event.name) {
      case 'checkout.loaded':
        markReady()
        break
      case 'checkout.completed':
        emit('complete', event)
        break
      case 'checkout.closed':
        emit('close')
        break
      case 'checkout.error':
        clearLoadedFallback()
        error.value = event.data?.error?.message || 'Checkout error occurred'
        emit('error', error.value)
        break
    }
  }

  /**
   * Wait for container element to be in DOM
   */
  const waitForContainer = (maxAttempts = 20): Promise<Element> => {
    return new Promise((resolve, reject) => {
      let attempts = 0
      const check = () => {
        const container = document.querySelector('.paddle-checkout-container')
        if (container) {
          resolve(container)
          return
        }
        attempts++
        if (attempts >= maxAttempts) {
          reject(new Error('Checkout container not found'))
          return
        }
        requestAnimationFrame(check)
      }
      check()
    })
  }

  /**
   * Initialize inline checkout with transaction ID
   */
  const initializeCheckout = async (): Promise<void> => {
    loading.value = true
    error.value = ''
    ready.value = false
    clearLoadedFallback()

    // (Re)subscribe to the shared Paddle event dispatcher, avoiding duplicate handlers.
    unsubscribeEvents?.()
    unsubscribeEvents = onPaddleEvent(handlePaddleEvent)

    try {
      const Paddle = await ensurePaddle()
      await waitForContainer()

      Paddle.Checkout.open({
        transactionId: props.transactionId,
        settings: {
          displayMode: 'inline',
          frameTarget: 'paddle-checkout-container',
          frameInitialHeight: 450,
          frameStyle:
            'width: 100%; min-width: 312px; background-color: transparent; border: none;',
          theme: 'light',
          locale: 'en',
        },
      })

      // Fallback in case checkout.loaded never fires (e.g. a swallowed event): don't strand the
      // user behind a permanent loading overlay on top of an already-rendered checkout.
      clearLoadedFallback()
      loadedFallbackTimer = setTimeout(() => {
        if (!ready.value) markReady()
      }, 10000)
    } catch (err: any) {
      console.error('Error initializing Paddle checkout:', err)
      clearLoadedFallback()
      error.value = 'Failed to initialize checkout. Please try again.'
      emit('error', error.value)
      loading.value = false
    }
  }

  /**
   * Retry checkout initialization
   */
  const retryCheckout = async () => {
    error.value = ''
    await initializeCheckout()
  }

  onMounted(async () => {
    await nextTick()
    await nextTick()
    await initializeCheckout()
  })

  onUnmounted(() => {
    clearLoadedFallback()
    unsubscribeEvents?.()
    unsubscribeEvents = null

    const Paddle = (window as any).Paddle
    if (Paddle?.Checkout?.close) {
      try {
        Paddle.Checkout.close()
      } catch (e) {
        console.warn('Error closing Paddle checkout:', e)
      }
    }
  })

  watch(
    () => props.transactionId,
    async (newId, oldId) => {
      if (newId && newId !== oldId) {
        await initializeCheckout()
      }
    },
  )
</script>

<style module>
  .inlineCheckout {
    width: 100%;
    min-height: 450px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
  }

  .loadingOverlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-16);
    padding: var(--space-32);
    color: var(--color-text-primary);
    background: var(--color-surface-default);
    z-index: 10;
  }

  .loadingOverlay p {
    margin: 0;
    color: var(--color-text-secondary);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--color-border-default);
    border-top-color: var(--color-interactive-primary);
    border-radius: var(--radius-full);
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-16);
    padding: var(--space-32);
    text-align: center;
    color: var(--color-status-error);
  }

  .error p {
    margin: 0;
  }

  .checkoutFrame {
    width: 100%;
    min-height: 450px;
  }
</style>
